// Squad formation math and objective-driven anchor movement, shared by the
// sim, the commander AI, and soldier AI.

import type { CharacterState, GameState, SquadState, Team } from './types';

export const SQUADS_PER_SIDE = 4;
export const SQUAD_SIZE = 10;
export const SQUAD_COLS = 5;
const SLOT_SPACING = 1.7;

export const ANCHOR_SPEED = 2.4;
/** Interceptors and rallying squads hustle. */
const ANCHOR_SPEED_FAST = 3.0;
/** Home line (rally) and push line (assault start) per team, in world X. */
export const HOME_X = 95;
export const ADVANCE_X = 132;

export function slotPosition(squad: SquadState, index: number): { x: number; z: number } {
  const col = index % SQUAD_COLS;
  const row = Math.floor(index / SQUAD_COLS);
  const lx = (col - (SQUAD_COLS - 1) / 2) * SLOT_SPACING;
  const lz = -(row * SLOT_SPACING + 0.9);
  const sinF = Math.sin(squad.facing);
  const cosF = Math.cos(squad.facing);
  // right = (cos f, -sin f), forward = (sin f, cos f)
  return {
    x: squad.anchorX + cosF * lx + sinF * lz,
    z: squad.anchorZ - sinF * lx + cosF * lz,
  };
}

/** An assault is a full storming: form up at the push line, then funnel
 * through the enemy gate, across the courtyard, and into the great hall. */
function assaultPath(squad: SquadState): { x: number; z: number }[] {
  const sign = squad.team === 'friendly' ? 1 : -1; // sign of the ENEMY castle
  return [
    { x: sign * ADVANCE_X, z: squad.laneZ },
    { x: sign * 150, z: 0 },
    { x: sign * 163.5, z: 0 }, // the gate
    { x: sign * 182, z: 0 }, // courtyard
    { x: sign * 199, z: 0 }, // through the keep door, into the corridor
    { x: sign * 220, z: 0 }, // through the hall door, into the great hall
  ];
}

/** True once an assaulting squad has fought its way into the enemy keep —
 * soldiers then hunt with extended range so a hiding King is found. */
export function inFinalAssault(squad: SquadState): boolean {
  return squad.objective === 'assault' && squad.advanceStep >= 4;
}

/** Centroid of a squad's living members (falls back to the anchor). */
export function squadCentroid(
  state: GameState,
  squadId: string
): { x: number; z: number; count: number } {
  let x = 0;
  let z = 0;
  let count = 0;
  for (const c of Object.values(state.characters)) {
    if (!c.alive || c.squadId !== squadId) continue;
    x += c.x;
    z += c.z;
    count++;
  }
  const squad = state.squads[squadId];
  if (count === 0) return { x: squad.anchorX, z: squad.anchorZ, count: 0 };
  return { x: x / count, z: z / count, count };
}

/** Move the anchor along its current objective. */
export function tickSquad(state: GameState, squad: SquadState, dt: number): void {
  const defaultFacing = squad.team === 'friendly' ? Math.PI / 2 : -Math.PI / 2;

  let target: { x: number; z: number } | null = null;
  let speed = ANCHOR_SPEED;
  switch (squad.objective) {
    case 'assault': {
      const path = assaultPath(squad);
      const step = Math.min(squad.advanceStep, path.length - 1);
      target = path[step];
      if (Math.hypot(target.x - squad.anchorX, target.z - squad.anchorZ) < 2) {
        if (step < path.length - 1) squad.advanceStep = step + 1;
      }
      break;
    }
    case 'intercept': {
      if (squad.targetSquad && state.squads[squad.targetSquad]) {
        const c = squadCentroid(state, squad.targetSquad);
        target = c.count > 0 ? { x: c.x, z: c.z } : { x: squad.targetX, z: squad.targetZ };
      } else {
        target = { x: squad.targetX, z: squad.targetZ };
      }
      speed = ANCHOR_SPEED_FAST;
      break;
    }
    case 'rally':
      target = { x: squad.targetX, z: squad.targetZ };
      speed = ANCHOR_SPEED_FAST;
      break;
    case 'hold':
    case 'garrison':
    case 'raid':
      target = { x: squad.targetX, z: squad.targetZ };
      break;
  }

  if (!target) {
    squad.facing = defaultFacing;
    return;
  }
  const dx = target.x - squad.anchorX;
  const dz = target.z - squad.anchorZ;
  const d = Math.hypot(dx, dz);
  if (d < 1) {
    squad.facing = squad.objective === 'intercept' ? squad.facing : defaultFacing;
    return;
  }
  squad.facing = Math.atan2(dx, dz);
  squad.anchorX += (dx / d) * speed * dt;
  squad.anchorZ += (dz / d) * speed * dt;
}

/** The squad's leader: its living member with the lowest formation index. */
export function squadLeader(state: GameState, squadId: string): CharacterState | null {
  let best: CharacterState | null = null;
  for (const c of Object.values(state.characters)) {
    if (!c.alive || c.squadId !== squadId) continue;
    if (!best || c.squadIndex < best.squadIndex) best = c;
  }
  return best;
}

/** Deterministic per-soldier temperament from the character id: aggression
 * scales how far a soldier will push to engage; discipline scales how far it
 * will stray from formation. No two soldiers fight quite alike. */
export function personality(id: string): { aggression: number; discipline: number } {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h = ((h ^ id.charCodeAt(i)) * 16777619) >>> 0;
  }
  return {
    aggression: 0.75 + (h % 1000) / 1000 * 0.55,
    discipline: 0.72 + ((h >>> 10) % 1000) / 1000 * 0.56,
  };
}

export function makeSquad(team: Team, index: number): SquadState {
  const sign = team === 'friendly' ? 1 : -1;
  const laneZ = (index - (SQUADS_PER_SIDE - 1) / 2) * 18;
  return {
    id: `${team}-squad-${index}`,
    team,
    objective: 'hold',
    targetX: -sign * 70,
    targetZ: laneZ,
    targetSquad: null,
    anchorX: -sign * 70,
    anchorZ: laneZ,
    laneZ,
    facing: sign * (Math.PI / 2),
    advanceStep: 0,
  };
}
